/**
 * AlbumContent.js
 *
 * Content section for album cards.
 * Displays album-specific information like photo count, trip association.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../shared/Icon';
import { formatDateRangeShort, formatDateShort } from '../../../utils/dateUtils';
import { VISIBILITY_CHIPS } from '../../../utils/visibilityChips';
import { albumCardTitle } from '../../../utils/recapFallbackTitle';

/**
 * Main AlbumContent Component
 *
 * Props:
 * @param {Object} data - Normalized card data
 */

const AlbumContent = ({ data, actions }) => {
  const { t } = useTranslation('feedCard');
  const { title, description, location, typeData } = data;
  const {
    placeCount = 0,
    tripName,
    tripLocation,
    tripStartDate,
    tripEndDate,
    visibility,
    isDayOut = false,
    listedInOwnTrip = false
  } = typeData || {};

  // An untitled recap is named by its day and places — the same function the
  // composer previewed under the blank title field, so the post reads the way
  // its author was told it would.
  const displayTitle = albumCardTitle(title, typeData);
  const visibilityChip = visibility ? VISIBILITY_CHIPS[visibility] : null;
  const cityLabel = tripLocation || location;
  // One day reads as that day, not a "range" of it ("Jun 14-14").
  const dateLabel = tripStartDate && tripEndDate
    ? (String(tripStartDate).slice(0, 10) === String(tripEndDate).slice(0, 10)
        ? formatDateShort(tripStartDate)
        : formatDateRangeShort(tripStartDate, tripEndDate))
    : null;
  // Separate items, not one pre-joined "a · b · c" string: joined, the whole
  // thing is a single unbreakable flex item, so it wrapped internally and then
  // shoved the visibility chip onto a third line — 66px for what fits in 44.
  // The "·" is still rendered, as its own flex child between items: unlike the
  // other cards' stats rows these entries carry no leading icon, so the gap
  // alone is not a visible boundary — and cityLabel contains its own comma
  // ("Valletta, Malta"), which would otherwise read as the separator.
  const stats = [
    placeCount > 0 ? t('albumContent.placeCount', { count: placeCount }) : null,
    cityLabel,
    dateLabel
  ].filter(Boolean);

  // The visibility chip doubles as the share-settings affordance for owners:
  // when the parent passes `actions.onShare`, render the chip as a button that
  // opens the SharingModal. Anonymous viewers / non-owners get the static chip.
  const onShare = actions?.onShare;

  return (
    <div className="flex-grow">
      {/* Title */}
      <h3 className="font-semibold text-content-primary line-clamp-2 mb-2">
        <Icon name="camera" className="w-4 h-4 inline mr-1" />{displayTitle}
      </h3>

      {/* Album stats row: places · city · date range */}
      {(stats.length > 0 || visibilityChip) && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-content-secondary mb-2">
          {stats.map((stat, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span aria-hidden="true" className="text-content-muted">·</span>}
              <span>{stat}</span>
            </React.Fragment>
          ))}
          {visibilityChip && (
            onShare ? (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShare(); }}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-background text-xs text-content-secondary border border-border hover:border-primary hover:text-primary transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2"
                title={t('albumContent.visibilityButton.title', { label: visibilityChip.label })}
                aria-label={t('albumContent.visibilityButton.ariaLabel', { label: visibilityChip.label })}
              >
                <Icon name={visibilityChip.icon} className="w-3 h-3" />
                <span>{visibilityChip.label}</span>
              </button>
            ) : (
              <span
                className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-background text-xs text-content-secondary border border-border"
                title={t('albumContent.visibilityChip.title', { label: visibilityChip.label })}
              >
                <Icon name={visibilityChip.icon} className="w-3 h-3" />
                <span>{visibilityChip.label}</span>
              </span>
            )
          )}
        </div>
      )}

      {/* Trip association — not for a Day Out, whose trip is the day itself,
          nor on the trip's own album list */}
      {tripName && !isDayOut && !listedInOwnTrip && (
        <div className="flex items-center text-xs text-content-muted mb-2">
          <Icon name="airplane" className="w-3 h-3 mr-1" />
          <span>{t('albumContent.fromTrip', { tripName })}</span>
        </div>
      )}

      {/* Description */}
      {description && (
        <p className="text-sm text-content-secondary mb-3 line-clamp-2">
          {description}
        </p>
      )}
    </div>
  );
};

export default memo(AlbumContent);
